import { useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Col from 'antd/es/col';
import Row from 'antd/es/row';
import Space from 'antd/es/space';
import Table from 'antd/es/table';
import Spin from 'antd/es/spin';
import Tooltip from 'antd/es/tooltip';
import { useUnit } from 'effector-react';
import { InfoOutlined } from '@ant-design/icons';
import {
  $sectorsList,
  downloadSectorsEv,
  getSectorsDataFx,
  SectorsGate,
} from '../../models/sectorsModel/index.js';
import { usePaginationControl } from '../../hooks/pagination-hook.js';
import FilterBlock from '../../components/FilterBlock/FilterBlock.jsx';
import Projects from '../../components/Projects/Projects.jsx';
import CircleGauge from '../../../components/UI/CircleGauge.jsx';
import { $matrixStagesMap } from '../../../models/globalModel/index.js';

function Sectors() {
  const { sector } = useParams();
  const [curPage, limit, onChangePagination, search] = usePaginationControl();
  const { count, data, manufacturers_count, tnved_count } =
    useUnit($sectorsList);

  const downloadSectors = useUnit(downloadSectorsEv);
  const navigate = useNavigate();
  const loading = useUnit(getSectorsDataFx.pending);
  const { pathname } = useLocation();
  const stagesMap = useUnit($matrixStagesMap);
  const stagesOpts = useMemo(() => {
    if (stagesMap && Object.keys(stagesMap).length > 0) {
      return Object.entries(stagesMap).map(([value, label]) => ({
        label,
        value,
      }));
    } else return [];
  }, [stagesMap]);

  let childrenField;
  let columns;
  let expTableColumn;
  let filters;

  switch (sector) {
    case 'products':
      {
        childrenField = 'manufacturers';
        columns = [
          {
            title: `ТНВЭД ЕАЭС (${tnved_count ?? 0})`,
            key: 'tnved',
            dataIndex: 'tnved',
          },
          {
            title: `Наименование ТНВЭД ЕАЭС`,
            key: 'tnved',
            dataIndex: 'tnved_name',
          },
          {
            title: `Кол-во ОТП:  (${manufacturers_count ?? 0})`,
            key: 'manufacturers_count',
            dataIndex: 'manufacturers_count',
          },
        ];
        expTableColumn = [
          {
            title: 'БИН',
            width: '5%',
            dataIndex: 'gov_id',
            key: 'exp_gov_id',
          },
          {
            title: 'Наименование',
            width: '5%',
            dataIndex: 'name',
            key: 'exp_name',
          },
          {
            title: 'ТНВЭД ЕАЭС',
            width: '5%',
            dataIndex: 'tnved',
            key: 'exp_tnved',
          },
          {
            title: 'Количество, ДВЦ',
            width: '5%',
            dataIndex: 'dvc',
            key: 'exp_dvc',
          },
          {
            title: 'Закупки',
            width: '5%',
            dataIndex: 'contract_count',
            key: 'exp_contract_count',
          },
          {
            title: 'Кол-во работников',
            width: '5%',
            dataIndex: 'employee_count',
            key: 'employee_count',
          },
          {
            title: 'Сумма уплаченных налогов',
            width: '5%',
            dataIndex: 'tax_count',
            key: 'tax_count',
          },
          {
            title: 'Регион',
            width: '5%',
            dataIndex: 'region',
            key: 'region',
          },
          {
            width: '5%',
            render: (text, record) => {
              return (
                <Link to={`/view-manufacturer/${record.id}`} target="_blank">
                  <Button icon={<InfoOutlined />} className="btn-primary" />
                </Link>
              );
            },
          },
        ];
        filters = [
          { name: 'tnved', label: 'ТНВЭД ЕАЭС', type: 'input' },
          {
            name: 'tnved_name',
            label: 'Наименование ТНВЭД ЕАЭС',
            type: 'input',
          },
          { name: 'employee_count', label: 'Кол-во работников', type: 'input' },
          {
            name: 'tax_count',
            label: 'Сумма уплаченных налогов',
            type: 'input',
          },
          // { name: 'bin', label: 'БИН', type: 'input' },
          // { name: 'address', label: 'Регион', type: 'input' },
        ];
      }
      break;
    case 'services':
      {
        childrenField = 'services';
        columns = [
          {
            title: 'ОКЭД',
            key: 'oked',
            dataIndex: 'oked',
          },
          {
            title: 'Наименование ОКЭД',
            key: 'oked_name',
            dataIndex: 'oked_name',
          },
        ];
        expTableColumn = [
          {
            title: 'БИН',
            width: '5%',
            dataIndex: 'gov_id',
            key: 'exp_gov_id',
          },
          {
            title: 'Наименование',
            width: '5%',
            dataIndex: 'name',
            key: 'exp_name',
          },
          {
            title: 'ОКЭД',
            width: '5%',
            dataIndex: 'oked',
            key: 'exp_oked',
          },
          {
            title: 'Наименование ОКЭД',
            width: '5%',
            dataIndex: 'oked_name',
            key: 'exp_oked_name',
          },
          {
            title: 'Регион',
            width: '5%',
            dataIndex: 'region',
            key: 'exp_region',
          },
          {
            width: '5%',
            render: (text, record) => {
              return (
                <Link
                  to={`/view-organization/${record.gov_id}`}
                  target="_blank"
                >
                  <Button icon={<InfoOutlined />} className="btn-primary" />
                </Link>
              );
            },
          },
        ];
        // filters = [
        //   { name: 'oked', label: 'ОКЭД', type: 'input' },
        //   { name: 'oked_name', label: 'Наименование ОКЭД', type: 'input' },
        //   { name: 'bin', label: 'БИН', type: 'input' },
        // ];
      }
      break;
    case 'works':
      {
        columns = [
          {
            title: 'Деятельность',
            width: '5%',
            dataIndex: 'activity',
            key: 'exp_activity',
          },
          {
            title: 'БИН',
            width: '5%',
            dataIndex: 'gov_id',
            key: 'exp_gov_id',
          },
          {
            title: 'Наименование',
            width: '5%',
            dataIndex: 'name',
            key: 'exp_name',
          },
          {
            title: 'Особые условия',
            width: '5%',
            dataIndex: 'special_condition',
            key: 'exp_special_condition',
            render: (text) => (text === 'None' || text === 'NULL' ? '-' : text),
          },
          {
            title: 'Подвид деятельности',
            width: '5%',
            dataIndex: 'types',
            key: 'types',
            render: (text) =>
              text && text.length > 60 ? (
                <Tooltip title={text}>{`${text.slice(0, 60)}...`}</Tooltip>
              ) : (
                text ?? '-'
              ),
          },
          {
            title: 'Опыт работы',
            width: '10%',
            dataIndex: 'work_experience',
            key: 'work_experience',
            render: (date) => (date ? `${date} мес.` : '-'),
          },
          {
            title: 'Кол-во сотрудников',
            width: '5%',
            dataIndex: 'workers',
            key: 'workers',
          },
          {
            title: 'Сумма упл. налогов',
            width: '5%',
            dataIndex: 'taxes',
            key: 'taxes',
            render: (val) =>
              Number.isFinite(val)
                ? new Intl.NumberFormat('ru-KZ', {
                    notation: 'standard',
                  }).format(val.toFixed(2))
                : '-',
          },
          {
            title: 'Регион',
            width: '5%',
            dataIndex: 'region',
            key: 'region',
          },
          {
            title: 'Статус',
            width: '5%',
            dataIndex: 'status',
            key: 'exp_status',
          },
          {
            width: '1%',
            align: 'center',
            render: (text, record) => {
              return (
                <Link
                  to={`/view-organization/${record.gov_id}`}
                  target="_blank"
                >
                  <Button icon={<InfoOutlined />} className="btn-primary" />
                </Link>
              );
            },
          },
        ];
        filters = [
          {
            name: 'activity',
            label: 'Деятельность',
            type: 'select',
            selectOptions: [
              {
                label: 'Изыскательская деятельность',
                value: 'Изыскательская деятельность',
              },
              {
                label: 'Проектная деятельность I категория',
                value: 'Проектная деятельность I категория',
              },
              {
                label: 'Проектная деятельность II категория',
                value: 'Проектная деятельность II категория',
              },
              {
                label: 'Проектная деятельность III категория',
                value: 'Проектная деятельность III категория',
              },
              {
                label: 'Строительно-монтажные работы I категория',
                value: 'Строительно-монтажные работы I категория',
              },
              {
                label: 'Строительно-монтажные работы II категория',
                value: 'Строительно-монтажные работы II категория',
              },
              {
                label: 'Строительно-монтажные работы III категория',
                value: 'Строительно-монтажные работы III категория',
              },
              { label: 'Прочее', value: 'Прочее' },
            ],
          },
          { name: 'bin', label: 'БИН', type: 'input' },
          {
            name: 'workers',
            label: 'Кол-во сотрудников',
            type: 'input',
          },
          {
            name: 'taxes',
            label: 'Сумма уплаченных налогов',
            type: 'input',
          },
          { name: 'work_experience', label: 'Опыт работы', type: 'input' },
          { name: 'region', label: 'Регион', type: 'input' },
          { name: 'sub_activity', label: 'Подвид деятельности', type: 'input' },
        ];
      }
      break;
    case 'projects':
      {
        columns = [];
        filters = [
          {
            name: 'category',
            label: 'Категория',
            type: 'select',
            selectOptions: [
              {
                label: 'ТС — сети теплоснабжения',
                value: 'ТС — сети теплоснабжения',
              },
              {
                label: 'ЭС - сети электроснабжения',
                value: 'ЭС - сети электроснабжения',
              },
              {
                label: 'ТМ - источники тепла',
                value: 'ТМ - источники тепла',
              },
              {
                label: 'НВ - наружные сети водоснабжения',
                value: 'НВ - наружные сети водоснабжения',
              },
              {
                label: 'НК - наружные сети канализации',
                value: 'НК - наружные сети канализации',
              },
              {
                label: 'КОО - канализационно-очистные сооружения',
                value: 'КОО - канализационно-очистные сооружения',
              },
              {
                label: 'ГЭ - генерация электроэнергии',
                value: 'ГЭ - генерация электроэнергии',
              },
            ],
          },
          { name: 'project', label: 'Проект', type: 'input' },
          {
            name: 'stage_id',
            label: 'Стадия',
            type: 'select',
            selectOptions: stagesOpts,
          },
          { name: 'otp_count', label: 'Кол-во ОТП', type: 'input' },
          { name: 'region', label: 'Регион', type: 'input' },
        ];
      }
      break;
  }

  // const onDownload = (values) => {
  //   downloadSectors({
  //     sector: sectors_dict[sector],
  //     search: values,
  //   });
  // };

  useEffect(() => {
    if (!sector) {
      navigate('/sectors/construction');
    }
  }, [sector]);

  let title;
  switch (sector) {
    case 'projects':
      title = 'Проекты';
      break;
  }

  const projectsGauges = [
    {
      title: 'Доля ОТП по Республике Казахстан',
      gauges: [
        {
          label: 'В натуральном выражении',
          value: 40,
          max: 100,
          percent: true,
          mainColor: 'var(--primary)',
          bgColor: 'var(--primary-bg)',
        },
        {
          label: 'В стоимостном выражении',
          value: 30,
          max: 100,
          percent: true,
          mainColor: 'var(--purple)',
          bgColor: 'var(--purple-light)',
        },
      ],
    },
    {
      title: 'Доля ОТП по региону',
      gauges: [
        {
          label: 'В натуральном выражении',
          value: 30,
          max: 100,
          percent: true,
          mainColor: 'var(--primary)',
          bgColor: 'var(--primary-bg)',
        },
        {
          label: 'В стоимостном выражении',
          value: 35,
          max: 100,
          percent: true,
          mainColor: 'var(--purple)',
          bgColor: 'var(--purple-light)',
        },
      ],
    },
  ];

  return (
    <>
      <SectorsGate sector={sector} search={search.toString()} />
      {title && <h2 style={{ marginBottom: 8 }}>{title}</h2>}
      <Col span={24} style={{ marginTop: 16 }}>
        {sector === 'projects' && (
          <Row gutter={24} style={{ width: '100%', marginBottom: 24 }}>
            {projectsGauges.map((item, index) => (
              <Col span={12} key={index}>
                <Card size={'small'} style={{ height: '100%', width: '100%' }}>
                  <Space
                    direction={'vertical'}
                    align={'center'}
                    style={{ width: '100%' }}
                  >
                    <h3 style={{ fontWeight: 500, marginBlock: 8 }}>
                      {item.title}
                    </h3>
                    <Row
                      justify={'center'}
                      align={'middle'}
                      style={{ gap: 128, width: '100%' }}
                    >
                      {item.gauges.map((gauge, idx) => (
                        <CircleGauge
                          value={gauge.value}
                          max={gauge.max}
                          key={idx}
                          size={150}
                          strokeWidth={12}
                          label={gauge.label}
                          percent={gauge.percent}
                          mainColor={gauge.mainColor}
                          bgColor={gauge.bgColor}
                          innerTextSize={24}
                          innerTextBold
                        />
                      ))}
                    </Row>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        {filters && filters?.length > 0 && (
          <Row>
            <FilterBlock
              items={filters}
              // download={onDownload}
            />
          </Row>
        )}
        {sector === 'projects' ? (
          <Spin spinning={loading}>
            <Projects data={data} />
          </Spin>
        ) : sector === 'works' ? (
          <Row style={{ marginTop: 24 }} id="sectors-table">
            <Table
              dataSource={data}
              columns={columns}
              style={{ width: '100%' }}
              loading={loading}
              bordered
              onChange={({ current, pageSize }) => {
                onChangePagination(current, pageSize);
                window.scrollTo({ top: 0 });
              }}
              pagination={{
                position: ['bottomCenter'],
                pageSize: parseInt(limit),
                current: parseInt(curPage),
                total: count,
              }}
            />
          </Row>
        ) : (
          <Row style={{ marginTop: 24 }} id="sectors-table">
            <Table
              dataSource={data}
              columns={columns}
              style={{ width: '100%' }}
              loading={loading}
              bordered
              onChange={({ current, pageSize }) => {
                onChangePagination(current, pageSize);
                window.scrollTo({ top: 0 });
              }}
              pagination={{
                position: ['bottomCenter'],
                pageSize: parseInt(limit),
                current: parseInt(curPage),
                total: count,
              }}
              expandable={
                pathname !== '/matrix/projects'
                  ? {
                      columnTitle: pathname.includes('products')
                        ? `Итого: ${count}`
                        : null,
                      expandedRowRender: (record) => {
                        return (
                          <Table
                            columns={expTableColumn}
                            dataSource={record[childrenField]}
                          />
                        );
                      },
                      rowExpandable: (record) =>
                        record[childrenField]?.length > 0,
                    }
                  : null
              }
            />
          </Row>
        )}
      </Col>
    </>
  );
}

export default Sectors;
